import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import type { Locale } from "../../../shared/types";
import * as api from "../lib/api";
import { describe } from "../lib/api";
import type { AskEvent, AskScope, AskSource } from "../lib/api";
import { useCopy } from "../lib/copy";

/**
 * Asking the collected data a question.
 *
 * The owner's words for it: "chatbot to talk with data and etc to make collected
 * data (which is most important) also talk to it's owner lol". Everything the
 * product spends money collecting — the notes, the transcripts, the files, the
 * studies — has until now only been readable as a rail of note titles and a
 * markdown export. This reads it back in a sentence.
 *
 * THREE THINGS IT DELIBERATELY IS NOT.
 *
 * It is not on the stage. The stage is a voice conversation with a consultant and
 * it holds its layout still through a turn; this is a text panel that opens over
 * the notes rail, in `position: fixed`, so nothing in the grid moves when it does.
 * The blob, the dock and the status line never learn it exists.
 *
 * It is not spoken. There is no microphone here and no playback: you are reading
 * your own record, and reading is faster than listening for exactly this.
 *
 * It is not a second consultant. It answers out of the record or it says it cannot,
 * and every claim it makes carries a reference to the row it came from. Those
 * references are rendered inline rather than swept into a footnote, because a
 * sentence without one is the thing you are meant to notice.
 */

interface DataChatProps {
  locale: Locale;
  /** Which record is being asked: a whole case, or one study inside it. */
  scope: AskScope;
  onClose: () => void;
}

/** One turn in the panel. The thread lives here and in no table. */
interface Entry {
  id: number;
  role: "user" | "assistant";
  text: string;
  sources: AskSource[];
  /** Set on the answer that did not land. Kept out of the history sent back up. */
  failed: boolean;
}

/**
 * Splits an answer into plain runs and the bracketed references between them, so
 * `[finding 42]` can be rendered as a mark rather than as punctuation. Text still
 * arriving mid-bracket simply has not matched yet and shows as itself for a frame,
 * which is honest and costs nothing.
 */
const REF = /\[([^\][\n]{1,120})\]/g;

function segments(text: string): { text: string; ref: boolean }[] {
  const out: { text: string; ref: boolean }[] = [];
  let last = 0;
  for (const match of text.matchAll(REF)) {
    const start = match.index;
    if (start > last) out.push({ text: text.slice(last, start), ref: false });
    out.push({ text: match[1] ?? "", ref: true });
    last = start + match[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), ref: false });
  return out;
}

export default function DataChat({ locale, scope, onClose }: DataChatProps) {
  const copy = useCopy(locale);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const nextId = useRef(1);

  // Escape closes, and the question box takes focus on open: this panel is opened
  // deliberately, by someone who is about to type.
  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // One in-flight question. Leaving the panel abandons it rather than letting it
  // finish into a component that is gone.
  useEffect(() => () => abortRef.current?.abort(), []);

  // The thread follows the answer down as it streams. Assigning scrollTop rather
  // than calling scrollIntoView: this runs on every delta, and scrollIntoView on a
  // container that is already at the bottom still animates in some browsers.
  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [entries]);

  const patchLast = useCallback((patch: (entry: Entry) => Entry): void => {
    setEntries((current) => {
      if (current.length === 0) return current;
      const out = current.slice();
      const last = out[out.length - 1];
      if (!last || last.role !== "assistant") return current;
      out[out.length - 1] = patch(last);
      return out;
    });
  }, []);

  const send = useCallback(async (): Promise<void> => {
    const question = draft.trim();
    if (!question || busy) return;

    // The thread as it stood BEFORE this question, and without any answer that
    // failed: sending a failure back up teaches the model that half-answers are a
    // shape it may produce.
    const history = entries
      .filter((entry) => !entry.failed && entry.text.trim().length > 0)
      .map((entry) => ({ role: entry.role, content: entry.text }));

    const askId = nextId.current++;
    const answerId = nextId.current++;
    setDraft("");
    setNotice(null);
    setEntries((current) => [
      ...current,
      { id: askId, role: "user", text: question, sources: [], failed: false },
      { id: answerId, role: "assistant", text: "", sources: [], failed: false },
    ]);
    setBusy(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await api.streamAsk(
        scope,
        question,
        history,
        (event: AskEvent) => {
          switch (event.type) {
            case "delta":
              patchLast((entry) => ({ ...entry, text: entry.text + event.text }));
              break;
            case "done":
              patchLast((entry) => ({ ...entry, text: event.answer, sources: event.sources }));
              break;
            case "error":
              setNotice(event.message);
              patchLast((entry) => ({ ...entry, failed: true }));
              break;
            default:
              // `thinking` needs nothing: the status line is already showing it.
              break;
          }
        },
        controller.signal,
      );
    } catch (err) {
      if (!controller.signal.aborted) {
        setNotice(describe(err, copy.askFailed));
        patchLast((entry) => ({ ...entry, failed: true }));
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
      // An unanswered question left in the thread reads as an answer that is still
      // coming. Drop the empty shell instead.
      setEntries((current) => current.filter((entry) => entry.role === "user" || entry.text.trim().length > 0));
      inputRef.current?.focus();
    }
  }, [busy, copy.askFailed, draft, entries, patchLast, scope]);

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Return sends, shift-Return is a new line. The same bargain as the dock.
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    void send();
  };

  const clear = (): void => {
    abortRef.current?.abort();
    setEntries([]);
    setNotice(null);
    inputRef.current?.focus();
  };

  return (
    <div className="ask" role="dialog" aria-modal="false" aria-labelledby="ask-title">
      <div className="ask__head">
        <div>
          <h2 id="ask-title" className="ask__title">
            {copy.askTitle}
          </h2>
          <p className="ask__sub label">{copy.askSub}</p>
        </div>
        <button className="ask__close label" type="button" onClick={onClose}>
          {copy.askClose}
        </button>
      </div>

      <div className="ask__thread" ref={threadRef}>
        {entries.length === 0 ? (
          <p className="ask__empty">{copy.askEmpty}</p>
        ) : (
          entries.map((entry) =>
            entry.role === "user" ? (
              <p className="ask__question" key={entry.id}>
                {entry.text}
              </p>
            ) : (
              <div className="ask__answer" key={entry.id}>
                <p className="ask__body">
                  {segments(entry.text).map((piece, i) =>
                    piece.ref ? (
                      // The reference, kept in place. It is the difference between a
                      // paragraph you can check and one you have to trust.
                      <span className="ask__ref num" key={i}>
                        {piece.text}
                      </span>
                    ) : (
                      <span key={i}>{piece.text}</span>
                    ),
                  )}
                </p>
                {entry.sources.length > 0 && (
                  <p className="ask__sources label">
                    <span className="ask__sources-label">{copy.askSourcesLabel}</span>
                    {entry.sources.map((source) => (
                      <span className="ask__source" key={source.ref}>
                        {copy.askSourceKind[source.kind]}
                        <span className="ask__source-label"> {source.label}</span>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            ),
          )
        )}
      </div>

      {/* One fixed place for every transient state this panel has, always present
          rather than conditionally rendered: a live region that is recreated is a
          live region nothing is watching, and a line that appears and disappears
          moves the composer under the reader's hands. */}
      <p className="ask__status label" aria-live="polite">
        {notice ?? (busy ? copy.askThinking : "")}
      </p>

      <div className="ask__composer">
        <textarea
          ref={inputRef}
          className="ask__input"
          rows={2}
          value={draft}
          placeholder={copy.askPlaceholder}
          aria-label={copy.askTitle}
          // NEVER disabled, mid-answer included. The one thing that must always
          // accept a keystroke is the box a person is typing into; `send` refuses a
          // second question while one is in flight, which is the same guarantee
          // without taking the field away.
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <div className="ask__actions">
          {entries.length > 0 && (
            <button className="ask__clear label" type="button" onClick={clear}>
              {copy.askClear}
            </button>
          )}
          <button
            className="btn btn--primary btn--sm"
            type="button"
            onClick={() => void send()}
            aria-disabled={busy || draft.trim().length === 0}
          >
            {copy.askSend}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Re-exported so a caller can build a scope without importing the api module. */
export type { AskScope };
